import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import useUserState from "~/stores/user";
import { getConfig } from "~/../lib/config";
import { getMsalToken } from "~/utils/msal";

export type ApiResponse<T> = {
  data?: T;
  error?: AxiosResponse<any, any>;
  status: number;
  statusText: string;
  ok: boolean;
};

export class BaseAPI {
  protected client: AxiosInstance;

  constructor() {
    let headers = {
      "Access-Control-Allow-Origin": true,
      "Content-Type": "application/json",
    } as Record<string, any>;

    const config = getConfig();
    this.client = axios.create({
      baseURL: config.BASE_URL,
      timeout: 10000, // 10 seconds
      headers: headers,
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const userState = useUserState.getState();
          if (
            !userState.sso &&
            (userState.hasTokenExpired() || !userState.isLoggedIn())
          ) {
            const reset = userState.reset;
            reset?.();
            console.warn(
              "Token expired or user not logged in, redirecting to login.",
            );

            window.location.href = "/login";
          }
        }

        return Promise.reject(error);
      },
    );
  }

  protected company(): number | null {
    const company = useUserState.getState().company;
    return company?.companyId ?? null;
  }

  protected user(): number | null {
    const user = useUserState.getState().user;
    return user?.userId ?? null;
  }

  protected sso(): boolean {
    return useUserState.getState().sso ?? false;
  }

  protected async getToken(): Promise<string | null> {
    if (this.sso()) {
      const token = await getMsalToken();
      return token;
    }

    const userStateToken = useUserState.getState().token;
    return userStateToken;
  }

  protected async _getConfig(): Promise<AxiosRequestConfig> {
    let token = await this.getToken();
    if (!token) return {};

    return {
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
      },
    };
  }

  protected async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    let response: AxiosResponse<any, any> | undefined = undefined;
    try {
      const config = await this._getConfig();
      response = await this.client.get<T>(endpoint, config);

      return {
        data: response.data as T,
        status: response.status,
        statusText: response.statusText,
        ok: response.status >= 200 && response.status < 300,
      };
    } catch (error: any) {
      return {
        error: error?.response?.data,
        status: error?.status ?? 500,
        statusText: response?.statusText ?? "Internal Server Error",
        ok: false,
      };
    }
  }

  protected async post<T>(
    endpoint: string,
    body: any,
  ): Promise<ApiResponse<T>> {
    let response: AxiosResponse<any, any> | undefined = undefined;
    try {
      const config = await this._getConfig();
      response = await this.client.post<T>(endpoint, body, config);

      return {
        data: response.data as T,
        status: response.status,
        statusText: response.statusText,
        ok: response.status >= 200 && response.status < 300,
      };
    } catch (error: any) {
      return {
        error: error?.response?.data,
        status: error?.status ?? 500,
        statusText: response?.statusText ?? "Internal Server Error",
        ok: false,
      };
    }
  }

  protected async put<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    let response: AxiosResponse<any, any> | undefined = undefined;
    try {
      const config = await this._getConfig();
      response = await this.client.put<T>(endpoint, body, config);

      return {
        data: response.data as T,
        status: response.status,
        statusText: response.statusText,
        ok: response.status >= 200 && response.status < 300,
      };
    } catch (error: any) {
      return {
        error: error?.response?.data,
        status: error?.status ?? 500,
        statusText: response?.statusText ?? "Internal Server Error",
        ok: false,
      };
    }
  }

  protected async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    let response: AxiosResponse<any, any> | undefined = undefined;
    try {
      const config = await this._getConfig();
      response = await this.client.delete<T>(endpoint, config);

      return {
        data: response.data as T,
        status: response.status,
        statusText: response.statusText,
        ok: response.status >= 200 && response.status < 300,
      };
    } catch (error: any) {
      return {
        error: error?.response?.data,
        status: error?.status ?? 500,
        statusText: response?.statusText ?? "Internal Server Error",
        ok: false,
      };
    }
  }
}
